import { useState } from 'react';
import { Stack, InputAdornment, IconButton, Link } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';

import { useRequestState } from '@app/hooks';
import { FormProvider, Iconify, RHFTextField } from '@app/components';
import { Strings } from '@app/constants';
import { PATH_AUTH } from '@app/routes/paths';
import { login } from '../slice';
import type { ILoginSchema } from '../types';
import { loginSchema } from '../validators';
import { selectLoginState } from '../selectors';

// --------------------------------------------

const LoginForm = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  const onClickEyeIcon = () => {
    setShowPassword(!showPassword);
  };

  const methods = useForm<ILoginSchema>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (form: ILoginSchema) => {
    dispatch(login(form));
    console.log(form);
  };

  const onClickForgotPassword = () => {
    const email = methods.getValues('email');
    navigate(PATH_AUTH.forgotPassword, { state: { email } });
  };
  const { loading } = useRequestState({
    stateSelector: selectLoginState,
    errorShown: true,
    successMessageShown: true,
  });

  return (
    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <RHFTextField
          data-testId="login.email"
          name="email"
          type="email"
          label={Strings.field.email}
          fullWidth
          helperText={Strings.auth.useYourOfficial}
        />
        <RHFTextField
          data-testId="login.password"
          name="password"
          label={Strings.field.password}
          fullWidth
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClickEyeIcon}>
                  {showPassword ? <Iconify icon="mdi:eye" /> : <Iconify icon="mdi:eye-off" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Link
          variant="body2"
          underline="always"
          color="inherit"
          href={PATH_AUTH.forgotPassword}
          sx={{ marginLeft: 'auto !important' }}
          onClick={onClickForgotPassword}
        >
          {Strings.auth.forgotPassword}
        </Link>

        <LoadingButton
          data-testId="login.submit"
          size="large"
          variant="contained"
          fullWidth
          type="submit"
          loading={loading}
        >
          {Strings.button.login}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
};

export { LoginForm };
